import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const BLUE = '#2563eb';
const BLUE_LIGHT = '#f0f9ff';
const WHITE = '#ffffff';
const TEXT = '#0a0a0a';
const TEXT_MUTED = '#666666';
const BORDER = '#e5e7eb';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const PREPOPULATE = {
  beginner: [],

  ready: [
    { item_id: 's1_aamc',       status: 'done' },
    { item_id: 's1_fap',        status: 'done' },
    { item_id: 's1_schools',    status: 'done' },
    { item_id: 's1_mcat_plan',  status: 'done' },
    { item_id: 's1_letters',    status: 'done' },
    { item_id: 's1_hours',      status: 'done' },
    { item_id: 's1_committee',  status: 'done' },
    { item_id: 's2_fap',        status: 'done' },
    { item_id: 's2_mcat_study', status: 'done' },
    { item_id: 's2_practice',   status: 'done' },
    { item_id: 's2_letters',    status: 'inprogress' },
    { item_id: 's2_committee',  status: 'inprogress' },
    { item_id: 's2_ps',         status: 'inprogress' },
    { item_id: 's2_transcripts_plan', status: 'done' },
  ],

  applying: [
    { item_id: 's1_aamc',       status: 'done' },
    { item_id: 's1_fap',        status: 'done' },
    { item_id: 's1_schools',    status: 'done' },
    { item_id: 's1_mcat_plan',  status: 'done' },
    { item_id: 's1_letters',    status: 'done' },
    { item_id: 's1_hours',      status: 'done' },
    { item_id: 's1_committee',  status: 'done' },
    { item_id: 's2_fap',        status: 'done' },
    { item_id: 's2_mcat_study', status: 'done' },
    { item_id: 's2_practice',   status: 'done' },
    { item_id: 's2_letters',    status: 'done' },
    { item_id: 's2_committee',  status: 'done' },
    { item_id: 's2_ps',         status: 'done' },
    { item_id: 's2_transcripts_plan', status: 'done' },
    { item_id: 's3_mcat',             status: 'done' },
    { item_id: 's3_transcripts',      status: 'done' },
    { item_id: 's3_amcas',            status: 'done' },
    { item_id: 's3_ps_final',         status: 'done' },
    { item_id: 's3_activities',       status: 'done' },
    { item_id: 's3_letters_confirm',  status: 'done' },
    { item_id: 's3_submit',           status: 'done' },
  ],
};

const PATHS = [
  {
    id: 'beginner',
    title: 'Just getting started',
    body: 'I have not taken the MCAT yet and am still preparing to apply.',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  },
  {
    id: 'ready',
    title: 'Ready to apply',
    body: 'I have my MCAT score and I am building my school list.',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  {
    id: 'applying',
    title: 'Currently applying',
    body: 'I have already submitted my primary application.',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0',
  },
];

const CYCLES = [
  {
    id: '2025-2026',
    label: '2025-2026 cycle',
    sub: 'Starting medical school Fall 2026',
  },
  {
    id: '2026-2027',
    label: '2026-2027 cycle',
    sub: 'Starting medical school Fall 2027',
  },
  {
    id: '2027-2028',
    label: '2027-2028 cycle',
    sub: 'Starting medical school Fall 2028',
  },
  {
    id: '2028-2029',
    label: '2028-2029 cycle',
    sub: 'Starting medical school Fall 2029',
  },
];

export default function Onboarding({ session, onComplete }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [path, setPath] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [saving, setSaving] = useState(false);

  async function finish() {
    if (!path || !cycle) return;
    setSaving(true);

    await supabase.from('profiles').upsert({
      id: session.user.id,
      onboarding_path: path,
      application_cycle: cycle,
      onboarding_complete: true,
    });

    const items = PREPOPULATE[path] || [];
    if (items.length > 0) {
      const rows = items.map(function(item) {
        return {
          user_id: session.user.id,
          item_id: item.item_id,
          status: item.status,
          done: item.status === 'done',
        };
      });
      await supabase.from('pre_app_checklist').upsert(rows, { onConflict: 'user_id,item_id' });
    }

    if (onComplete) onComplete();
    navigate('/dashboard');
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : '2rem' }}>

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 48 }}>
        <div style={{ width: 32, height: 32, background: BLUE, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={WHITE} strokeWidth="2.5" strokeLinecap="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
          </svg>
        </div>
        <span style={{ fontSize: 20, fontWeight: 800, color: TEXT }}>
          Pre<span style={{ color: BLUE }}>Visr</span>
        </span>
      </div>

      {/* Card */}
      <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 16, padding: isMobile ? '24px 20px' : '40px 44px', width: '100%', maxWidth: 560, boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 32 }}>
          <div style={{ height: 3, borderRadius: 2, flex: 1, background: BLUE }} />
          <div style={{ height: 3, borderRadius: 2, flex: 1, background: step === 2 ? BLUE : BORDER, transition: 'background .3s' }} />
        </div>

        {step === 1 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: TEXT_MUTED, marginBottom: 8 }}>Step 1 of 2</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: TEXT, letterSpacing: '-0.5px', marginBottom: 6 }}>
              Where are you in your journey?
            </div>
            <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 28, lineHeight: 1.5 }}>
              This helps us show you exactly what you need right now -- no noise, no irrelevant steps.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PATHS.map(function(p) {
                const selected = path === p.id;
                return (
                  <div key={p.id} onClick={() => setPath(p.id)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 18px', borderRadius: 10, border: '1px solid ' + (selected ? BLUE : BORDER), background: selected ? BLUE_LIGHT : WHITE, cursor: 'pointer', transition: 'all .15s' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: selected ? BLUE : '#f8fafc', border: '1px solid ' + (selected ? BLUE : BORDER), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' }}>
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={selected ? WHITE : TEXT_MUTED} strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d={p.icon} />
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: selected ? BLUE : TEXT, marginBottom: 3 }}>{p.title}</div>
                      <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>{p.body}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', flexShrink: 0, width: 18, height: 18, borderRadius: '50%', border: '2px solid ' + (selected ? BLUE : BORDER), background: selected ? BLUE : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: WHITE }} />}
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={() => { if (path) setStep(2); }}
              disabled={!path}
              style={{ width: '100%', marginTop: 24, padding: '12px', borderRadius: 8, border: 'none', background: path ? BLUE : BORDER, color: path ? WHITE : TEXT_MUTED, fontSize: 14, fontWeight: 700, cursor: path ? 'pointer' : 'not-allowed', boxShadow: path ? '0 4px 10px rgba(37,99,235,0.25)' : 'none', transition: 'all .15s' }}>
              Continue
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: TEXT_MUTED, marginBottom: 8 }}>Step 2 of 2</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: TEXT, letterSpacing: '-0.5px', marginBottom: 6 }}>
              Which cycle are you targeting?
            </div>
            <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 28, lineHeight: 1.5 }}>
              We use this to show you real calendar dates for your sprint timeline.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {CYCLES.map(function(c) {
                const selected = cycle === c.id;
                return (
                  <div key={c.id} onClick={() => setCycle(c.id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderRadius: 10, border: '1px solid ' + (selected ? BLUE : BORDER), background: selected ? BLUE_LIGHT : WHITE, cursor: 'pointer', transition: 'all .15s' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: selected ? BLUE : TEXT }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 2 }}>{c.sub}</div>
                    </div>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid ' + (selected ? BLUE : BORDER), background: selected ? BLUE : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: WHITE }} />}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button onClick={() => setStep(1)}
                style={{ flex: 1, padding: '12px', borderRadius: 8, border: '1px solid ' + BORDER, background: WHITE, color: TEXT_MUTED, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Back
              </button>
              <button onClick={finish}
                disabled={!cycle || saving}
                style={{ flex: 2, padding: '12px', borderRadius: 8, border: 'none', background: cycle ? BLUE : BORDER, color: cycle ? WHITE : TEXT_MUTED, fontSize: 14, fontWeight: 700, cursor: cycle ? 'pointer' : 'not-allowed', boxShadow: cycle ? '0 4px 10px rgba(37,99,235,0.25)' : 'none', transition: 'all .15s' }}>
                {saving ? 'Setting up...' : 'Take me to my dashboard'}
              </button>
            </div>
          </>
        )}

      </div>

      {/* Skip */}
      <button onClick={() => { if (onComplete) onComplete(); navigate('/dashboard'); }}
        style={{ marginTop: 16, background: 'transparent', border: 'none', fontSize: 12, color: TEXT_MUTED, cursor: 'pointer' }}>
        Skip for now
      </button>

    </div>
  );
}
